import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import { QRCodeSVG } from "qrcode.react";

const DiveShareModal = ({ open, onClose, sharedDiveUrl, isMobile }) => {
  const handleCopyUrl = () => {
    navigator.clipboard.writeText(sharedDiveUrl);
    // You could add a toast notification here
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Join My Dive Log",
          text: "Scan this QR code or use this link to add your details to our shared dive:",
          url: sharedDiveUrl,
        });
      } catch (error) {
        console.log("Error sharing:", error);
        handleCopyUrl(); // Fallback to copy
      }
    } else {
      handleCopyUrl(); // Fallback for browsers without Web Share API
    }
  };

  const handleDownloadQR = () => {
    const svg = document.getElementById("dive-share-qrcode");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = "dive-share-qrcode.png";
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Share Your Dive</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          gap={3}
          sx={{ p: 1 }}
        >
          <Typography variant="h5" gutterBottom textAlign="center">
            Shared Dive QR Code
          </Typography>

          <Typography variant="body1" textAlign="center">
            Share this QR code with your dive team so they can add their details
            to the dive log:
          </Typography>

          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#f5f5f5",
            }}
          >
            <QRCodeSVG
              id="dive-share-qrcode"
              value={sharedDiveUrl}
              size={isMobile ? 180 : 200}
              level="H" // High error correction allows for more logo space
              includeMargin={true}
              renderAs="svg"
              fgColor="#0066CC" // Primary QR code color - customize to match your brand
              bgColor="#FFFFFF" // Background color
              imageSettings={{
                src: "/favicon.ico", // Path to your logo
                x: undefined,
                y: undefined,
                height: 40,
                width: 40,
                excavate: true, // This creates space for the logo
              }}
            />

            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              sx={{ mt: 2 }}
              onClick={handleDownloadQR}
            >
              Download QR Code
            </Button>
          </Paper>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Or share this link directly:
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              width: "100%",
              wordBreak: "break-all",
              backgroundColor: "grey.50",
            }}
          >
            <Typography variant="body2" fontFamily="monospace">
              {sharedDiveUrl}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleCopyUrl} variant="outlined">
          Copy Link
        </Button>
        <Button
          onClick={handleShare}
          variant="contained"
          startIcon={<ShareIcon />}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiveShareModal;
